import { c as _c } from "react/compiler-runtime";
import { mapDefaults } from "@/info/map-defaults";
import type { Filters } from "@/types/filters";
import { logger } from "@/utils/logger";
import { useCallback } from "react";
interface UseDataFetchOrchestratorParams {
  mode: "default" | "project";
  fetchMapData: (params: {
    filters: Partial<Filters>;
    append?: boolean;
  }) => Promise<void>;
  fetchNearByProjects: (params: {
    filters: Partial<Filters>;
  }) => Promise<void>;
}
interface OrchestrateParams {
  filters: Partial<Filters>;
  map?: google.maps.Map | null;
  filtersForGraphQL: Partial<Filters>;
  isInitialFetch?: boolean; // Flag to indicate first post-SSR fetch
}
export function useDataFetchOrchestrator(t0) {
  const $ = _c(6);
  const {
    mode,
    fetchMapData,
    fetchNearByProjects
  } = t0;
  let t1;
  if ($[0] !== fetchMapData || $[1] !== fetchNearByProjects || $[2] !== mode) {
    t1 = async t2 => {
      const {
        filters,
        map,
        filtersForGraphQL,
        isInitialFetch: t3
      } = t2;
      const isInitialFetch = t3 === undefined ? false : t3;
      if (mode === "project") {
        const shouldForceZoom = !!filters.selectedLocationId;
        if (shouldForceZoom && map) {
          return new Promise((resolve, reject) => {
            let timeoutId = null;
            let listener = null;
            const cleanup = () => {
              if (listener) {
                listener.remove();
                listener = null;
              }
              if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
              }
            };
            const performFetch = () => {
              const mapBounds = map.getBounds();
              if (!mapBounds) {
                fetchMapData({
                  filters: filtersForGraphQL
                }).then(() => resolve()).catch(reject);
                return;
              }
              const boundsData = {
                north: mapBounds.getNorthEast().lat(),
                south: mapBounds.getSouthWest().lat(),
                east: mapBounds.getNorthEast().lng(),
                west: mapBounds.getSouthWest().lng()
              };
              const nearbyProjectsFilters = {
                ...filters,
                bounds: [boundsData]
              };
              const {
                bounds: _omit,
                ...filtersForGraphQLWithoutBounds
              } = filtersForGraphQL;
              fetchMapData({
                filters: filtersForGraphQLWithoutBounds
              }).then(() => fetchNearByProjects({
                filters: nearbyProjectsFilters
              })).then(() => {
                resolve();
              }).catch(error => {
                reject(error);
              });
            };
            const currentZoom = map.getZoom();
            if (currentZoom !== mapDefaults.projectZoom) {
              map.setZoom(mapDefaults.projectZoom);
              let zoomListener = null;
              const originalCleanup = cleanup;
              const enhancedCleanup = () => {
                originalCleanup();
                if (zoomListener) {
                  zoomListener.remove();
                  zoomListener = null;
                }
              };
              const checkZoom = () => {
                if (Math.round(map.getZoom() || 0) === mapDefaults.projectZoom) {
                  if (zoomListener) {
                    zoomListener.remove();
                    zoomListener = null;
                  }
                }
              };
              zoomListener = map.addListener("zoom_changed", checkZoom);
              listener = map.addListener("idle", () => {
                enhancedCleanup();
                performFetch();
              });
              timeoutId = setTimeout(() => {
                enhancedCleanup();
                logger.warn("Map idle timeout reached, proceeding with data fetch");
                performFetch();
              }, 5000);
            } else {
              performFetch();
            }
          });
        } else {
          if (map) {
            const mapBounds_0 = map.getBounds();
            if (mapBounds_0) {
              const boundsData_0 = {
                north: mapBounds_0.getNorthEast().lat(),
                south: mapBounds_0.getSouthWest().lat(),
                east: mapBounds_0.getNorthEast().lng(),
                west: mapBounds_0.getSouthWest().lng()
              };
              const nearbyProjectsFilters_0 = {
                ...filters,
                bounds: [boundsData_0]
              };
              const {
                bounds: _omit_0,
                ...filtersForGraphQLWithoutBounds_0
              } = filtersForGraphQL;
              return fetchMapData({
                filters: filtersForGraphQLWithoutBounds_0
              }).then(() => fetchNearByProjects({
                filters: nearbyProjectsFilters_0
              }));
            }
          }
          return fetchMapData({
            filters: filtersForGraphQL
          });
        }
      } else {
        const filterKeys = Object.keys(filtersForGraphQL);
        const hasOnlyBoundsFilter = filterKeys.length === 1 && filterKeys[0] === "bounds" && !!filtersForGraphQL.bounds;
        const shouldAppend = isInitialFetch && hasOnlyBoundsFilter;
        return fetchMapData({
          filters: filtersForGraphQL,
          append: shouldAppend
        }).catch(fetchError => {
          logger.graphqlError(fetchError, "MAP_DATA_FETCH_FILTERS", filtersForGraphQL);
          throw fetchError;
        });
      }
    };
    $[0] = fetchMapData;
    $[1] = fetchNearByProjects;
    $[2] = mode;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const orchestrateFetch = t1;
  let t2;
  if ($[4] !== orchestrateFetch) {
    t2 = {
      orchestrateFetch
    };
    $[4] = orchestrateFetch;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}